import { ActionType } from "@xala/common-services";
import { IFormValues, NotificationService } from "@xala/common-ui";
import { FormInstance } from "antd/lib/form";
import { TFunction } from "i18next";

const notificationService = NotificationService.getInstance();

export function getRequiredFieldNamesForActionType(
  actionType: ActionType | undefined
): string[] {
  if (actionType == null) {
    return [];
  }
  switch (actionType) {
    case ActionType.NoAction:
      return [];
    case ActionType.OpenMedia:
      return ["AssetId", "AssetTitle"];
    case ActionType.OpenMediaCollection:
      return ["CollactionId", "CollectionName"];
    case ActionType.OpenScreen:
      return ["ScreenType", "ScreenId", "ScreenName"];
    case ActionType.OpenUrl:
      return ["Url"];
    case ActionType.CallTo:
    case ActionType.EmailTo:
      return [];
    default:
      return [];
  }
}

export function isActionTypeFieldValuePresent(value: unknown): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  if (typeof value === "number") {
    return !Number.isNaN(value);
  }
  return true;
}

/**
 * Ensures the current action type has all required form values set and passes Ant Design rules where defined.
 */
export async function validateAssetActionTypesForm(
  form: FormInstance<IFormValues>,
  actionType: ActionType | undefined,
  t: TFunction
): Promise<boolean> {
  if (actionType == null) {
    notificationService.error({
      message: t(
        "ASSET_ACTION_TYPE_REQUIRED",
        "Please select an action type."
      ),
    });
    return false;
  }

  const requiredKeys = getRequiredFieldNamesForActionType(actionType);
  if (requiredKeys.length === 0) {
    return true;
  }

  const values = form.getFieldsValue();
  const missing = requiredKeys.filter(
    (key) => !isActionTypeFieldValuePresent(values[key])
  );

  if (missing.length > 0) {
    form.setFields(
      missing.map((name) => ({
        name,
        errors: [t("FIELD_REQUIRED", "This field is required")],
      }))
    );
    notificationService.error({
      message: t(
        "ASSET_ACTION_TYPES_VALIDATION_ERROR",
        "Please complete all required fields for this action."
      ),
    });
    return false;
  }

  try {
    await form.validateFields(requiredKeys);
    return true;
  } catch {
    return false;
  }
}
